import { Button, Grid } from "@mui/material";
import { useFormik } from "formik";
import { isEmpty, startCase } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getRealm } from "../../../../../AuthProvider";
import { SegmentTabs } from "../../../../../components/SegmentTabs";
import Flex from "../../../../../components/Flex";
import LoadingOverlay from "../../../../../components/LoadingOverlay";
import SuccessModal, {
  useSuccess,
} from "../../../../../components/SuccessModal";
import Text from "../../../../../components/Text";
import { useFormScrollIntoView } from "../../../../../hooks/use-form-scrollInto-view";
import { useQueryParams } from "../../../../../hooks/use-query-param";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { usePatientIntakeFormCreate } from "../../../../../queries/patient-portal/forms/use-patient-intake-form-create";
import { usePatientIntakeFormList } from "../../../../../queries/patient-portal/forms/use-patient-intake-form-list";
import { usePatientIntakeFormUpdate } from "../../../../../queries/patient-portal/forms/use-patient-intake-form-update";
import useCurrentUser from "../../../../../hooks/local-storage/use-current-user";
import {
  DEFAULT_UUID_NAME_LIST,
  DEF_PAGINATION,
  getErrorSnackbar,
  getSuccessSnackbar,
  transformMultiSelectFrontEndVal,
} from "../../../../../utils/utils";
import { STATE_LIST } from "../../../../../constants";
import { useGetPatient } from "../../../../../queries/use-get-patient-uuid";
import { Patient } from "../../../../../services";
import { IntakeFormSection } from "../appointment/encounter/content-section/intake-preview/IntakePreviewModal";
import { HippaConsentButton } from "./HIPAAConsentForm";
import IntakeBasicDetails from "./IntakeBasicDetails";
// import IntakeInsuranceInformation from "./IntakeInsuranceInformation";
import IndividualPsychotherapy from "./IndividualPsychotherapy";
import IntakeMedicalHistory from "./IntakeMedicalHistory";
import IntakePaymentPolicies from "./IntakePaymentPolicies";
import IntakePrescriptionPolicy from "./IntakePrescriptionPolicy";
import { MedicalServicesAgreementButton } from "./MedicalServicesAgreementButton";
import { PhysicianPatientArbitrationAgreementButton } from "./PhysicianPatientArbitrationAgreementButton";
import {
  IntakeFormValues,
  initialValues,
  validationSchema,
} from "./new-intake-form-contant/intake-constant";
import { usePatientNewIntakeFormList } from "../../../../../queries/patient-portal/forms/use-patient-new-intake-form-list";
import ClientInformationForm from "./ClinicalHistoryFormComponents/ClientInformationForm";
import ClientHistoryForm from "./ClinicalHistoryFormComponents/ClientHistoryForm";
import ClientInsuranceForm from "./ClinicalHistoryFormComponents/ClientInsuranceForm";
import ConsentForServicesForm from "./ClinicalHistoryFormComponents/ConsentForServicesForm";
import AIConsentForm from "./ClinicalHistoryFormComponents/AIConsentForm";
import PrivacyPracticesForm from "./ClinicalHistoryFormComponents/PrivacyPracticesForm";
import { useGetProviderGroupPracticeInfo } from "@/queries/use-get-provider-group-practice-info";
import ReleaseOfInformationForm from "./ClinicalHistoryFormComponents/ReleaseOfInformationForm";
import { getViewAs } from "../../../../../utils/auth-storage";
import {
  INTAKE_FORM_SECTIONS_MAPPER,
  TELEHEALTH_CONSENT_FORM,
  PHI_RELEASE_CONSENT_FORM,
} from "@/pages/app/provider/scheduling/appointment/appointment-constant";
import CcmConsentForm, {
  ccmConsentSchema,
} from "@/pages/app/provider/consent-forms/ccm-consent/CcmConsentForm";
import PatientConsentForm, {
  patientConsentSchema,
} from "@/pages/app/provider/consent-forms/patient-consent/PatientConsentForm";
import { buildCcmConsentPrefill } from "@/pages/app/provider/consent-forms/ccm-consent/ccm-consent-utils";
import { getIntakeFormValues, hasIntakeForm } from "./intake-form-utils";
import { buildPatientConsentPrefill } from "@/pages/app/provider/consent-forms/patient-consent/patient-consent-utils";

interface NewPatientIntakeFormProps {
  isPublic?: boolean;
  viewOnly?: boolean;
  setActiveTab?: ({ ind, val }: { ind: number; val: string }) => void;
  prefillData?: any;
  showOnlyIntakeForms?: IntakeFormSection[];
  showOnlyAssessmentTabs?: string[];
  appointmentId?: string;
  patientId?: string;
  actionId?: string;
  // Comma-separated form ids, parallel to showOnlyIntakeForms names. Passed by
  // the patient-portal dashboard action so submit can send the id per form.
  formEntries?: string;
  onClose?: () => void;
}

const NewPatientIntakeForm = ({
  isPublic,
  viewOnly,
  setActiveTab,
  prefillData,
  showOnlyIntakeForms,
  showOnlyAssessmentTabs,
  appointmentId: appointmentIdProp,
  patientId: patientIdProp,
  actionId: actionIdProp,
  formEntries: formEntriesProp,
  onClose,
}: NewPatientIntakeFormProps) => {
  // useQueryParams is a hook, so every read has to run unconditionally; the
  // `prop ?? queryParam` fallbacks below short-circuited the call and changed
  // the hook order between renders.
  const patientIdQueryParam = useQueryParams("patientId");
  const appointmentIdQueryParam = useQueryParams("appointmentId");
  const assessmentsQueryParam = useQueryParams("assessments");
  const intakeFormQueryParam = useQueryParams("intakeform");
  const patientId = patientIdProp ?? patientIdQueryParam;
  const formEntriesQueryParam = useQueryParams("formEntries");
  const params = (() => {
    const params = new URLSearchParams(window.location.search);
    return Object.fromEntries(params.entries());
  })();

  const appointmentUuid = appointmentIdProp ?? appointmentIdQueryParam;
  const encounterAppointmentId = useQueryParams("appointmentid");

  const hostNameArr = location.hostname?.split(".");
  const showSnackbar = useSnackbar();
  const xTENANTID = getRealm(hostNameArr);
  const { mutateAsync: updateIntakeForm } =
    usePatientIntakeFormUpdate(isPublic);
  const { data: practiceInfo } = useGetProviderGroupPracticeInfo({
    xTENANTID,
    isPublic,
  });
  const isPatientPortal = getViewAs() === "patient";

  const {
    data: oldPatientIntakeForm,
    refetch: reftchOldPatientIntakeForm,
    isRefetching: isReftchingOldPatientIntakeForm,
    isLoading: isLoadingOldPatientIntakeForm,
  } = usePatientIntakeFormList({
    appointmentUuid: appointmentUuid ?? encounterAppointmentId ?? undefined,
    patientUuid: patientId ?? undefined,
    isPublic,
    pagination: DEF_PAGINATION,
    xTENANTID,
    enabled: !isPatientPortal,
  });

  const {
    data: newPatientIntakeForm,
    refetch: newRefetch,
    isRefetching: newIsRefetching,
    isLoading: newIsLoading,
  } = usePatientNewIntakeFormList({
    patientUuid: patientId ?? "",
    // appointmentUuid: appointmentUuid ?? encounterAppointmentId ?? undefined,
    pagination: DEF_PAGINATION,
    xTENANTID,
    enabled: isPatientPortal,
  });

  const refetch = () => {
    isPatientPortal ? newRefetch() : reftchOldPatientIntakeForm();
  };

  const isRefetching = isPatientPortal
    ? newIsRefetching
    : isReftchingOldPatientIntakeForm;

  const isLoading = isPatientPortal
    ? newIsLoading
    : isLoadingOldPatientIntakeForm;

  const prefillIntakeForm = isPatientPortal
    ? oldPatientIntakeForm
    : newPatientIntakeForm;

  const [me] = useCurrentUser();

  // Client Information Form auto-populate source. Time zone, phones, marital
  // status, languages and address live on the patient record, not on the
  // session user, so they are read from the patient itself. The public
  // (email link) page is unauthenticated and prefills from URL params instead.
  // useGetPatient already unwraps to the patient itself; its declared
  // Result<any> wrapper is stale, hence the cast.
  const { data: patientData } = useGetPatient(patientId ?? undefined, isPublic);
  const patientDetails = patientData as unknown as Patient | undefined;

  const prefillIntakeFormData =
    prefillData?.content || prefillIntakeForm?.data?.content[0]?.content;

  const assessmentTabs =
    showOnlyAssessmentTabs ?? assessmentsQueryParam?.split(",");

  const intakeForms = showOnlyIntakeForms?.length
    ? showOnlyIntakeForms
    : (intakeFormQueryParam?.split(",") ??
      prefillData?.activeSections ??
      prefillIntakeForm?.data?.content[0]?.activeSections?.split(","));

  const FORM_TABS_DATA = intakeForms?.map((form: string) => ({
    value: form,
    label: formatIntakeFormLabel(form),
  }));

  // Build the form-name -> id lookup once (patient portal). formEntriesProp
  // ("id1,id2") is in the same order as the form tabs, so on save we read the
  // id of the saved tab directly instead of re-deriving it every submit.
  const formIdByName = useMemo(() => {
    const ids = formEntriesProp?.split(",")?.map((id) => id.trim()) ?? [];
    return (intakeForms ?? []).reduce(
      (acc: Record<string, string>, name: string, index: number) => {
        acc[name] = ids[index] ?? "";
        return acc;
      },
      {}
    );
  }, [formEntriesProp, intakeForms?.join(",")]);

  const [curTab, setCurTab] = useState<string | undefined>(intakeForms?.[0]);

  const selectedIntakeForms = curTab;
  const isCcmConsentTab = hasIntakeForm(curTab, TELEHEALTH_CONSENT_FORM);
  const isPatientConsentTab = hasIntakeForm(curTab, PHI_RELEASE_CONSENT_FORM);

  const { mutateAsync: createIntakeForm } =
    usePatientIntakeFormCreate(isPublic);
  const navigate = useNavigate();
  const submitActionRef = useRef<"submit" | "saveAndNext">("submit");

  const handleSubmit = (values: IntakeFormValues) => {
    // Patient portal: send only the saved form's id, read directly from the
    // prebuilt form-name -> id map. Provider / public open-page flows keep the
    // original name + URL-param behaviour.
    const currentFormEntryId = curTab ? (formIdByName[curTab] ?? "") : "";

    const curTabIndex = FORM_TABS_DATA.findIndex(
      (form: any) => form.value === curTab
    );

    const formEntries = formEntriesQueryParam?.split(",");
    const filledForms = isPatientPortal
      ? currentFormEntryId
      : formEntries?.length
        ? formEntries?.[curTabIndex]
        : curTab
          ? startCase(curTab)
          : "";

    // Submission source for the chronological event: the public assessment
    // page is opened from the email link sent to the patient; every other
    // entry point is the authenticated patient portal.
    const submissionMethod = isPublic ? "Email Link" : "Patient Portal";

    const payload = {
      patientIntakeForm: {
        uuid: prefillIntakeForm?.data?.content[0]?.uuid,
        appointment: appointmentUuid ?? encounterAppointmentId ?? undefined,
        patient: { [patientId ?? ""]: "" },
        activeSections: intakeForms?.join(","),
        content: JSON.stringify(values),
        actionId: actionIdProp ?? undefined,
        filledForms,
        submissionMethod,
      },
      formEntries: isPatientPortal
        ? currentFormEntryId
        : formEntriesQueryParam || "",
      xTENANTID,
    };

    if (submitActionRef.current === "submit") {
      console.error("Submitting form with payload:", payload);
    }

    (prefillIntakeFormData
      ? updateIntakeForm(payload)
      : createIntakeForm(payload)
    )
      .then((res) => {
        if (submitActionRef.current === "submit") {
          // Show the success modal only on the public assessment page
          // (isPublic without actionId). Patient portal modal and provider
          // portal rely on the snackbar alone and just close.
          const isPublicAssessment = isPublic && !actionIdProp;

          if (!isPublicAssessment) {
            onClose?.();
          } else if (!isAssFormContainNull) {
            openSuccessIntake();
          } else {
            navigate("/public/submit-success");
          }
        }

        showSnackbar(getSuccessSnackbar(res));
        refetch();
      })
      .catch((err) => {
        showSnackbar(getErrorSnackbar(err));
      });
  };

  useEffect(() => {
    if (prefillIntakeFormData) return;

    const hasParamsDetails =
      !!params?.name || !!params?.email || !!params?.phone;

    if (hasParamsDetails) {
      const firstName = params.name?.split(" ")[0];
      const lastName = params.name?.split(" ")[1];
      const keys = ["line1", "line2", "city", "state", "country", "zipcode"];

      const address = params.address
        ?.split(", ")
        ?.reduce((acc: any, item: string, index: number) => {
          const [value] = item.split(":");
          acc[keys[index]] = value;
          return acc;
        }, {});

      const fullName = params.name ?? "";
      const dob = params.dob ? new Date(params.dob) : null;

      formik.setFieldValue("firstName", firstName);
      formik.setFieldValue("lastName", lastName);
      formik.setFieldValue("birthDate", dob);
      formik.setFieldValue("mobileNumber", params.phone);
      formik.setFieldValue("email", params.email);
      formik.setFieldValue("sex", params.gender);
      formik.setFieldValue("socialSecurity", params.ssn);
      formik.setFieldValue("address", address);

      formik.setFieldValue("paymentPolicyPatientName", fullName);
      formik.setFieldValue("paymentPolicyPrintedName", fullName);
      formik.setFieldValue("paymentPolicyDob", dob);
    } else if (me) {
      const fullName = `${me?.firstName ?? ""} ${me?.lastName ?? ""}`.trim();
      const dob = me?.birthDate ? new Date(me.birthDate) : null;

      formik.setFieldValue("firstName", me?.firstName);
      formik.setFieldValue("lastName", me?.lastName);
      formik.setFieldValue("birthDate", dob);
      formik.setFieldValue("mobileNumber", me?.mobileNumber ?? me?.phone);
      formik.setFieldValue("email", me?.email);
      formik.setFieldValue("sex", me?.gender);
      formik.setFieldValue("socialSecurity", me?.ssn);
      // Address is owned by the patient-record effect below whenever the
      // patient is loaded, so the two effects cannot race over it.
      if (!patientDetails) formik.setFieldValue("address", me?.address);

      formik.setFieldValue("paymentPolicyPatientName", fullName);
      formik.setFieldValue("paymentPolicyPrintedName", fullName);
      formik.setFieldValue("paymentPolicyDob", dob);
    }
  }, [me, patientDetails, prefillIntakeFormData]);

  // Client Information Form fields that only exist on the patient record.
  // Runs after the effect above so the patient's own values win over the
  // session user's for the fields both can supply (address).
  useEffect(() => {
    if (prefillIntakeFormData || !patientDetails) return;

    formik.setFieldValue("timezone", patientDetails?.timezone ?? "");
    formik.setFieldValue("mobilePhone", patientDetails?.mobileNumber ?? "");
    formik.setFieldValue("maritalStatus", patientDetails?.maritalStatus ?? "");
    formik.setFieldValue(
      "languages",
      transformMultiSelectFrontEndVal(
        DEFAULT_UUID_NAME_LIST,
        patientDetails?.languages
      ) ?? []
    );
    formik.setFieldValue("address", {
      ...(patientDetails?.address ?? {}),
      // The patient record stores address.state as the STATE_LIST id ("1"),
      // while the intake form's state dropdown is keyed by the label
      // ("Alabama (AL)"), so the id is mapped across before it is set.
      state: toIntakeStateValue(patientDetails?.address?.state),
    });
  }, [patientDetails, prefillIntakeFormData]);

  const formik = useFormik({
    initialValues: prefillIntakeFormData
      ? JSON.parse(prefillIntakeFormData)
      : initialValues,
    validationSchema: validationSchema(
      selectedIntakeForms ? [selectedIntakeForms] : []
    ).shape({
      ...(isCcmConsentTab && {
        [TELEHEALTH_CONSENT_FORM.value]: ccmConsentSchema,
      }),
      ...(isPatientConsentTab && {
        [PHI_RELEASE_CONSENT_FORM.value]: patientConsentSchema,
      }),
    }),
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  const {
    values,
    setFieldValue,
    handleChange,
    handleBlur,
    touched,
    errors,
    submitCount,
    submitForm,
    validateForm,
  } = formik;

  // Race, language and emergency contact come from the patient record, or from
  // the emailed link ("name, mobile, RELATIONSHIP") on the public page.
  const [contactName, contactMobile, contactRelationship] =
    params.emergencyContact?.split(", ") ?? [];
  const consentPrefillSource = {
    ...values,
    race: patientDetails?.race ?? params.race,
    languages: patientDetails?.languages ?? { link: params.language },
    emergencyContacts: patientDetails?.emergencyContacts ?? [
      {
        firstName: contactName,
        mobile: contactMobile,
        relationshipWithPatient: contactRelationship,
      },
    ],
  };

  useFormScrollIntoView(formik);

  const isAssFormContainNull =
    assessmentTabs?.includes("null") || !assessmentTabs?.length;

  const [
    successIntake,
    { open: openSuccessIntake, close: closeSuccessIntake },
  ] = useSuccess(() => {
    if (assessmentTabs?.length) setActiveTab?.({ ind: 1, val: "screening" });
    onClose?.();
  });

  const handleSkipAndNext = () => {
    const nextTabInd = intakeForms?.findIndex(
      (form: string) => form === curTab
    );
    const nextTab = intakeForms?.[(nextTabInd ?? 0) + 1];

    setCurTab(nextTab);
  };

  const handleSaveAndNext = async () => {
    submitActionRef.current = "saveAndNext";
    // submitForm resolves even when invalid; stay on the tab so its errors show.
    const formErrors = await validateForm();
    await submitForm();
    if (isEmpty(formErrors)) handleSkipAndNext();
  };

  return (
    <>
      <SuccessModal
        label={
          prefillIntakeFormData
            ? "Successfully Updated Intake Form"
            : "Successfully Submitted Intake Form"
        }
        opened={successIntake}
        onClose={closeSuccessIntake}
      />

      <LoadingOverlay isLoading={isLoading && isRefetching} />
      <SegmentTabs
        value={curTab}
        onChange={(value) => setCurTab(value)}
        tabsData={FORM_TABS_DATA}
      />

      <Grid container spacing={2} mt={2}>
        {(selectedIntakeForms?.includes("demographics") ||
          selectedIntakeForms?.includes("Demographics")) && (
          <Grid item xs={12}>
            <IntakeBasicDetails
              viewOnly={viewOnly}
              values={values}
              setFieldValue={setFieldValue}
              handleChange={handleChange}
              handleBlur={handleBlur}
              touched={touched}
              errors={errors}
            />
          </Grid>
        )}
        {/* note : client information form is added for the new Demographics. - patient open URL view */}
        {(selectedIntakeForms?.includes("clientInformationForm") ||
          selectedIntakeForms?.includes("Client Information Form")) && (
          <Grid item xs={12}>
            <ClientInformationForm
              viewOnly={viewOnly}
              values={values}
              setFieldValue={setFieldValue}
              handleChange={handleChange}
              handleBlur={handleBlur}
              touched={touched}
              errors={errors}
            />
          </Grid>
        )}
        {(selectedIntakeForms?.includes("clientHistoryForm") ||
          selectedIntakeForms?.includes("Client History Form")) && (
          <Grid item xs={12}>
            <ClientHistoryForm
              viewOnly={viewOnly}
              values={values}
              setFieldValue={setFieldValue}
              handleChange={handleChange}
              handleBlur={handleBlur}
              touched={touched}
              errors={errors}
            />
          </Grid>
        )}
        {(selectedIntakeForms?.includes("clientInsuranceForm") ||
          selectedIntakeForms?.includes("Client Insurance Form")) && (
          <Grid item xs={12}>
            <ClientInsuranceForm
              viewOnly={viewOnly}
              values={values}
              setFieldValue={setFieldValue}
              handleChange={handleChange}
              handleBlur={handleBlur}
              touched={touched}
              errors={errors}
              patientId={patientId ?? undefined}
              isPublic={isPublic}
            />
          </Grid>
        )}
        {/* Legacy Insurance section — replaced by the Client Insurance Form.
            It also leaked into that tab: the check is a substring match and
            "clientInsuranceForm" contains "Insurance".
        {(selectedIntakeForms?.includes("insurance") ||
          selectedIntakeForms?.includes("Insurance")) && (
          <Grid item xs={12}>
            <IntakeInsuranceInformation
              viewOnly={viewOnly}
              values={values}
              setFieldValue={setFieldValue}
              handleChange={handleChange}
              handleBlur={handleBlur}
              touched={touched}
              errors={errors}
            />
          </Grid>
        )}
        */}
        {(selectedIntakeForms?.includes("paymentPolicy") ||
          selectedIntakeForms?.includes("Payment Policy")) && (
          <Grid item xs={12}>
            <IntakePaymentPolicies
              viewOnly={viewOnly}
              submitCount={submitCount}
              values={values}
              setFieldValue={setFieldValue}
              handleChange={handleChange}
            />
          </Grid>
        )}
        {(selectedIntakeForms?.includes("prescription") ||
          selectedIntakeForms?.includes("Prescription")) && (
          <Grid item xs={12}>
            <IntakePrescriptionPolicy
              viewOnly={viewOnly}
              submitCount={submitCount}
              values={values}
              setFieldValue={setFieldValue}
              handleChange={handleChange}
              handleBlur={handleBlur}
              touched={touched}
              errors={errors}
            />
          </Grid>
        )}
        {(selectedIntakeForms?.includes("patientMedicalHistory") ||
          selectedIntakeForms?.includes("Patient Medical History")) && (
          <Grid item xs={12}>
            <IntakeMedicalHistory
              viewOnly={viewOnly}
              values={values}
              setFieldValue={setFieldValue}
              handleChange={handleChange}
              handleBlur={handleBlur}
              touched={touched}
              errors={errors}
            />
          </Grid>
        )}
        <Grid item xs={12} container>
          <Grid item xs={12}>
            {(selectedIntakeForms?.includes("HIPAAConsent") ||
              selectedIntakeForms?.includes("HIPAA Consent")) && (
              <HippaConsentButton
                viewOnly={viewOnly}
                values={values}
                setFieldValue={setFieldValue}
                errors={errors}
              />
            )}
          </Grid>
          <Grid item xs={12}>
            {(selectedIntakeForms?.includes("consentForServices") ||
              selectedIntakeForms?.includes("Consent For Services")) && (
              <ConsentForServicesForm
                viewOnly={viewOnly}
                values={values}
                setFieldValue={setFieldValue}
                handleChange={handleChange}
                handleBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            )}
          </Grid>
          <Grid item xs={12}>
            {(selectedIntakeForms?.includes("aiConsent") ||
              selectedIntakeForms?.includes(
                "Consent For Use Of Artificial Intelligence"
              )) && (
              <AIConsentForm
                viewOnly={viewOnly}
                values={values}
                setFieldValue={setFieldValue}
                handleChange={handleChange}
                handleBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            )}
          </Grid>
          <Grid item xs={12}>
            {(selectedIntakeForms?.includes("privacyPractices") ||
              selectedIntakeForms?.includes("Notice Of Privacy Practices")) && (
              <PrivacyPracticesForm
                viewOnly={viewOnly}
                practiceInfo={practiceInfo}
                values={values}
                setFieldValue={setFieldValue}
                handleChange={handleChange}
                handleBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            )}
          </Grid>
          <Grid item xs={12}>
            {(selectedIntakeForms?.includes("releaseOfInformation") ||
              selectedIntakeForms?.includes("Release Of Information")) && (
              <ReleaseOfInformationForm
                viewOnly={viewOnly}
                values={values}
                setFieldValue={setFieldValue}
                handleChange={handleChange}
                handleBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            )}
          </Grid>
          {isCcmConsentTab && (
            <Grid item xs={12}>
              <CcmConsentForm
                initialValues={
                  getIntakeFormValues(
                    formik.initialValues,
                    TELEHEALTH_CONSENT_FORM
                  ) ?? buildCcmConsentPrefill(consentPrefillSource)
                }
                readOnly={viewOnly}
                submitCount={submitCount}
                onValuesChange={(consentValues) =>
                  setFieldValue(TELEHEALTH_CONSENT_FORM.value, consentValues)
                }
              />
            </Grid>
          )}
          {isPatientConsentTab && (
            <Grid item xs={12}>
              <PatientConsentForm
                initialValues={
                  getIntakeFormValues(
                    formik.initialValues,
                    PHI_RELEASE_CONSENT_FORM
                  ) ?? buildPatientConsentPrefill(consentPrefillSource)
                }
                readOnly={viewOnly}
                submitCount={submitCount}
                onValuesChange={(consentValues) =>
                  setFieldValue(PHI_RELEASE_CONSENT_FORM.value, consentValues)
                }
              />
            </Grid>
          )}
          <Grid item xs={12}>
            {(selectedIntakeForms?.includes("medicalServicesAgreement") ||
              selectedIntakeForms?.includes("Medical Services Agreement")) && (
              <MedicalServicesAgreementButton
                isPreviewVisible={showOnlyIntakeForms?.includes(
                  "medicalServicesAgreement"
                )}
                viewOnly={viewOnly}
                setFieldValue={setFieldValue}
                values={values}
                errors={errors}
              />
            )}
          </Grid>
          <Grid item xs={12}>
            {(selectedIntakeForms?.includes(
              "physicianPatientArbitrationAgreement"
            ) ||
              selectedIntakeForms?.includes(
                "Physician Patient Arbitration Agreement"
              )) && (
              <PhysicianPatientArbitrationAgreementButton
                viewOnly={viewOnly}
                setFieldValue={setFieldValue}
                values={values}
                errors={errors}
              />
            )}
          </Grid>
          <Grid item xs={12}>
            {(selectedIntakeForms?.includes("INDIVIDUAL_PSYCHOTHERAPY") ||
              selectedIntakeForms?.includes("Individual Psychotherapy")) && (
              <IndividualPsychotherapy
                viewOnly={viewOnly}
                values={values}
                setFieldValue={setFieldValue}
                handleChange={handleChange}
                handleBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            )}
          </Grid>
          {/* // note : in future we will add original form  */}
          <Grid item xs={12}>
            {(selectedIntakeForms?.includes("PSYCHOEDUCATION_THERAPY") ||
              selectedIntakeForms?.includes("Psychoeducation Therapy")) && (
              <IndividualPsychotherapy
                viewOnly={viewOnly}
                values={values}
                setFieldValue={setFieldValue}
                handleChange={handleChange}
                handleBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            )}
          </Grid>
        </Grid>
      </Grid>
      {!viewOnly && (
        <Flex justifyContent={"flex-end"} mr={2} mb={1} gap={1}>
          {intakeForms?.[intakeForms?.length - 1] !== curTab && (
            <Button onClick={handleSkipAndNext} variant="outlined">
              <Text>Skip & Next</Text>
            </Button>
          )}
          {intakeForms?.[intakeForms?.length - 1] !== curTab && (
            <Button onClick={handleSaveAndNext} variant="contained">
              <Text>Save & Next</Text>
            </Button>
          )}
          {/* ECRH-4137: per BA, Submit on every page is replaced by a single
              "Save" button on the last tab only. Submit button JSX kept here
              commented for discoverability — do not delete.
          <Button
            onClick={() => {
              submitActionRef.current = "submit";
              submitForm();
            }}
            variant="contained"
          >
            <Text>Submit</Text>
          </Button>
          */}
          {intakeForms?.[intakeForms?.length - 1] === curTab && (
            <Button
              onClick={() => {
                submitActionRef.current = "submit";
                submitForm();
              }}
              variant="contained"
            >
              <Text>Save</Text>
            </Button>
          )}
        </Flex>
      )}
    </>
  );
};

export default NewPatientIntakeForm;

const formatIntakeFormLabel = (form: string) =>
  INTAKE_FORM_SECTIONS_MAPPER[form] ?? startCase(form);

/**
 * Patient addresses are saved with the STATE_LIST id ("1"); the intake form's
 * state dropdown (STATE_STRING_LIST) is keyed by the label ("Alabama (AL)").
 * Values that are already labels are passed through untouched.
 */
const toIntakeStateValue = (state?: string) =>
  STATE_LIST.find((item) => item.value === state)?.label ?? state ?? "";
